/**
 * Theme context — shared across the whole app.
 *
 * Previously, useTheme() was a local hook with its own useState, which
 * meant every component that called it had an INDEPENDENT copy of the
 * theme state. Changing the theme in /settings only re-rendered the
 * settings page; AppShell and the rest of the tree kept the stale value
 * until a page reload.
 *
 * Now useTheme() reads from a React Context created by <ThemeProvider>
 * at the root of the tree, so every consumer sees the same state and
 * re-renders together when setTheme() is called.
 *
 * The provider also keeps the <html data-theme="..."> attribute in sync
 * with the active theme, which is how the CSS tokens from themes/*.css
 * take effect.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { DEFAULT_THEME, getTheme, type Theme } from "../themes";

const STORAGE_KEY = "black-wall-theme";

interface ThemeContextValue {
  theme: Theme;
  themeId: string;
  setTheme: (id: string) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themeId, setThemeId] = useState<string>(() => {
    if (typeof window === "undefined") return DEFAULT_THEME;
    return localStorage.getItem(STORAGE_KEY) ?? DEFAULT_THEME;
  });

  const theme = useMemo<Theme>(
    () => getTheme(themeId) ?? getTheme(DEFAULT_THEME)!,
    [themeId],
  );

  useEffect(() => {
    document.documentElement.dataset.theme = themeId;
    try {
      localStorage.setItem(STORAGE_KEY, themeId);
    } catch {
      // ignore quota / private mode issues — the UI still works,
      // just doesn't persist across reloads.
    }
  }, [themeId]);

  const setTheme = useCallback((id: string) => {
    if (getTheme(id)) {
      setThemeId(id);
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, themeId, setTheme }),
    [theme, themeId, setTheme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    // Fallback: if for some reason someone forgets the provider, we
    // return a frozen default so the app keeps rendering instead of
    // crashing with "useTheme must be used within a ThemeProvider".
    return {
      theme: getTheme(DEFAULT_THEME)!,
      themeId: DEFAULT_THEME,
      setTheme: () => {
        // eslint-disable-next-line no-console
        console.warn(
          "[useTheme] called outside <ThemeProvider>. setTheme is a no-op.",
        );
      },
    };
  }
  return ctx;
}

export type { Theme };
